/** Opens a Payroll section from outside the hub (for example a Calendar marker).
 * PayrollView is lazily loaded, so a section event sent while it is unmounted
 * has no listener. The request is therefore parked here and read when the hub
 * mounts, and also sent as a live event for the case where it is already open.
 *
 * Two limits stop a stale request redirecting a later visit: it lapses after
 * REQUEST_TTL_MS (long enough for a slow chunk load), and browser Back or
 * Forward drops it. The mount effect clears it once it has been used.
 *
 * The controller's section is set before the view changes, so the address is
 * written once (/payroll/employees) and Back returns to where the person was.
 */

const REQUEST_TTL_MS = 30_000;

let pending: { sectionId: string; employeeId?: string; at: number } | null = null;

export function clearRequestedPayrollSection(): void {
  pending = null;
}

// Peek rather than take: React may run a state initialiser twice in development,
// and the second run must see the same answer. The mount effect clears it.
export function peekRequestedPayrollSection(now: number = Date.now()): string | null {
  return pending && now - pending.at <= REQUEST_TTL_MS ? pending.sectionId : null;
}

export function peekRequestedPayrollEmployee(now: number = Date.now()): string | null {
  return pending?.employeeId && now - pending.at <= REQUEST_TTL_MS ? pending.employeeId : null;
}

export function openPayrollSection(sectionId: string, employeeId?: string): void {
  pending = { sectionId, employeeId, at: Date.now() };
  window.addEventListener('popstate', clearRequestedPayrollSection, { once: true });
  window.dispatchEvent(new CustomEvent('payroll:section-change', { detail: { sectionId } }));
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view: 'payroll' } }));
  window.dispatchEvent(new CustomEvent('payroll:navigate-section', { detail: { sectionId, employeeId } }));
}
